import { Container, Graphics } from 'pixi.js';
import { Button } from './Button';
import { UI_COLORS } from './Theme';

export type HeaderBarAction = 'save' | 'evidence' | 'caseList' | 'settings' | 'title';

export interface HeaderBarOptions {
  width?: number;
  height?: number;
  actions?: readonly HeaderBarAction[];
  disabled?: boolean;
  disabledActions?: readonly HeaderBarAction[];
  lockOnAction?: boolean;
  onAction?: (action: HeaderBarAction) => void;
}

export interface HeaderBarState {
  disabled: boolean;
  actionLocked: boolean;
  disabledActions: HeaderBarAction[];
}

interface HeaderButtonConfig {
  action: HeaderBarAction;
  label: string;
  width: number;
}

const HEADER_BUTTONS: HeaderButtonConfig[] = [
  { action: 'save', label: '保存', width: 104 },
  { action: 'evidence', label: '証拠', width: 104 },
  { action: 'caseList', label: '事件', width: 104 },
  { action: 'settings', label: '設定', width: 104 },
  { action: 'title', label: '戻る', width: 104 }
];

const DEFAULT_HEADER_ACTIONS: readonly HeaderBarAction[] = [
  'evidence',
  'caseList',
  'settings',
  'title'
];

export class HeaderBar extends Container {
  private readonly background = new Graphics();
  private readonly buttons = new Map<HeaderBarAction, Button>();
  private readonly actionConfigs: HeaderButtonConfig[];
  private barWidth: number;
  private barHeight: number;
  private disabled = false;
  private actionLocked = false;
  private lockOnAction: boolean;
  private disabledActions = new Set<HeaderBarAction>();
  private onAction?: (action: HeaderBarAction) => void;

  public constructor(options: HeaderBarOptions = {}) {
    super();
    this.barWidth = options.width ?? 520;
    this.barHeight = options.height ?? 58;
    this.disabled = options.disabled ?? false;
    this.lockOnAction = options.lockOnAction ?? true;
    this.disabledActions = new Set(options.disabledActions ?? []);
    this.onAction = options.onAction;
    this.actionConfigs = normalizeHeaderActionConfigs(options.actions ?? DEFAULT_HEADER_ACTIONS);

    this.addChild(this.background);
    for (const config of this.actionConfigs) {
      const button = new Button({
        label: config.label,
        width: config.width,
        height: 42,
        variant: config.action === 'title' ? 'ghost' : 'secondary',
        onPress: () => this.handleAction(config.action)
      });
      this.buttons.set(config.action, button);
      this.addChild(button);
    }

    this.draw();
  }

  public setOnAction(onAction: ((action: HeaderBarAction) => void) | undefined): void {
    this.onAction = onAction;
  }

  public setDisabled(disabled: boolean): void {
    this.disabled = disabled;
    if (!disabled) {
      this.actionLocked = false;
    }
    this.applyDisabledState();
  }

  public releaseActionLock(): void {
    this.actionLocked = false;
    this.applyDisabledState();
  }

  public setActionDisabled(action: HeaderBarAction, disabled: boolean): void {
    if (disabled) {
      this.disabledActions.add(action);
    } else {
      this.disabledActions.delete(action);
    }

    this.applyDisabledState();
  }

  public setDisabledActions(actions: readonly HeaderBarAction[]): void {
    this.disabledActions = new Set(actions);
    this.applyDisabledState();
  }

  public setSize(width: number, height = this.barHeight): void {
    this.barWidth = width;
    this.barHeight = height;
    this.draw();
  }

  public getState(): HeaderBarState {
    return {
      disabled: this.disabled,
      actionLocked: this.actionLocked,
      disabledActions: [...this.disabledActions]
    };
  }

  private handleAction(action: HeaderBarAction): void {
    if (this.disabled || this.actionLocked || this.disabledActions.has(action)) {
      return;
    }

    if (this.lockOnAction) {
      this.actionLocked = true;
      this.applyDisabledState();
    }

    try {
      this.onAction?.(action);
    } catch (error) {
      this.actionLocked = false;
      this.applyDisabledState();
      throw error;
    }
  }

  private draw(): void {
    this.background.clear();
    this.background
      .roundRect(0, 0, this.barWidth, this.barHeight, 8)
      .fill({ color: UI_COLORS.black, alpha: 0.56 })
      .stroke({ color: UI_COLORS.gold, width: 1, alpha: 0.45 });
    this.background.rect(0, this.barHeight - 4, this.barWidth, 2).fill({
      color: UI_COLORS.red,
      alpha: 0.62
    });

    const horizontalPadding = 12;
    const preferredGap = 10;
    const compactGap = 6;
    const availableWidth = Math.max(0, this.barWidth - horizontalPadding * 2);
    const totalPreferredWidth =
      this.actionConfigs.reduce((width, config) => width + config.width, 0) +
      (this.actionConfigs.length - 1) * preferredGap;
    const gap = availableWidth >= totalPreferredWidth ? preferredGap : compactGap;
    const sharedButtonWidth = Math.max(
      56,
      Math.min(
        this.actionConfigs[0]?.width ?? 104,
        Math.floor(
          (availableWidth - (this.actionConfigs.length - 1) * gap) / this.actionConfigs.length
        )
      )
    );
    const totalButtonsWidth =
      sharedButtonWidth * this.actionConfigs.length + (this.actionConfigs.length - 1) * gap;
    let cursorX = Math.max(12, this.barWidth - totalButtonsWidth - 12);

    for (const config of this.actionConfigs) {
      const button = this.buttons.get(config.action);
      if (!button) {
        continue;
      }

      button.setSize(sharedButtonWidth, 42);
      button.position.set(cursorX, Math.round((this.barHeight - 42) / 2));
      cursorX += sharedButtonWidth + gap;
    }

    this.applyDisabledState();
  }

  private applyDisabledState(): void {
    for (const [action, button] of this.buttons.entries()) {
      button.setDisabled(this.disabled || this.actionLocked || this.disabledActions.has(action));
    }
  }
}

function normalizeHeaderActionConfigs(actions: readonly HeaderBarAction[]): HeaderButtonConfig[] {
  const seen = new Set<HeaderBarAction>();
  const configs: HeaderButtonConfig[] = [];

  for (const action of actions) {
    if (seen.has(action)) {
      continue;
    }

    const config = HEADER_BUTTONS.find((candidate) => candidate.action === action);
    if (!config) {
      continue;
    }

    seen.add(action);
    configs.push(config);
  }

  return configs.length > 0
    ? configs
    : HEADER_BUTTONS.filter((config) => DEFAULT_HEADER_ACTIONS.includes(config.action));
}
